// TimerContext — the shell-owned running timer (Epic 4 · S4.2).
//
// The running timer lives in the app SHELL, on every hub page, startable from a
// work card or a thread — nobody navigates to Effort to start timing. The Effort
// page only *consumes* this timer to render its hero + stop dialog.
//
// This is the canonical home for the timer. The Effort page now consumes it
// directly: its hero reads useTimer() here, and EffortContent hydrate()s the
// DB-running timer from fn_my_timesheet into it — so the hero, the shell timer
// widget, and anything started from a task or thread are ONE timer, not two
// disconnected copies. (The old interim effort mirror provider is deleted.)
//
// Contract (the shape the hero + stop dialog consume):
//   useTimer() -> {
//     running: { id, started_at, title, client, client_id, suggested, needs_review } | null,
//     elapsedSeconds: number,
//     start(attachment): Promise,      // attachment -> fn_timer_start params
//     stop(description, reason): Promise<{ id, minutes, description, needs_review }>,
//   }
//
// fn_timer_start -> { id, started_at, stopped_previous, suggested }. One running
// timer per person (DB index); starting a second stops the first WITHOUT erroring
// (stopped_previous) — we just adopt the new running timer. `needs_review` is true
// over 8h and is surfaced for correction, never silently accepted (StopTimerDialog).
//
// RPC shapes are UNCONFIRMED (see api/hub/effort.js). Verified to EXIST on the live
// Supabase on 2026-09-06 (P0001 "no current team member" with the anon key — they
// need a real JWT). See docs/CLIENTHUB-RPC-GAPS.md.

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { startTimer, stopTimer } from '../api/hub/effort';
import { useElapsed } from '../hooks/useElapsed';

const TimerContext = createContext(null);

// The running timer's display label lives under `what` on a fn_my_timesheet timer
// (the Effort page) and under `title` on one just started via the shell. Mirror the
// two so every consumer reads a populated label whichever way the timer entered —
// the hero reads `what`, the shell TimerWidget reads `title`, and a timer with only
// one of them no longer shows as "Untitled work" in the other.
const withLabel = (r) => {
  if (!r) return r;
  const label = r.what ?? r.title ?? null;
  return { ...r, what: r.what ?? label, title: r.title ?? label };
};

/**
 * @param {object} props
 * @param {object|null} [props.initialRunning] Seed the running timer — a page
 *   that already knows a timer is running (e.g. a timesheet's `running`) passes
 *   it so the shell shows the timer on first paint. Adopted once on mount.
 * @param {React.ReactNode} props.children
 */
export function TimerProvider({ initialRunning = null, children }) {
  const [running, setRunning] = useState(withLabel(initialRunning) || null);
  const elapsedSeconds = useElapsed(running?.started_at);

  const start = useCallback(async (attachment = {}) => {
    const res = await startTimer({
      taskId: attachment.taskId ?? attachment.task_id ?? null,
      threadId: attachment.threadId ?? attachment.thread_id ?? null,
      clientId: attachment.clientId ?? attachment.client_id ?? null,
      meetingId: attachment.meetingId ?? attachment.meeting_id ?? null,
      note: attachment.note ?? null,
    });
    // fn_timer_start -> { id, started_at, stopped_previous, suggested }
    setRunning(withLabel({
      id: res?.id,
      started_at: res?.started_at,
      title: attachment?.title ?? res?.title ?? null,
      client: attachment?.client ?? res?.client ?? null,
      client_id: attachment?.clientId ?? attachment?.client_id ?? res?.client_id ?? null,
      suggested: res?.suggested ?? attachment?.note ?? null,
      needs_review: res?.needs_review ?? null,
    }));
    return res;
  }, []);

  const stop = useCallback(async (description, reason = null) => {
    const res = await stopTimer(description, reason);
    setRunning(null);
    return res;
  }, []);

  // Adopt a server-known running timer (e.g. the Effort page's fn_my_timesheet
  // `.running`) WITHOUT clobbering one the user started elsewhere this session. The
  // app-root provider mounts before any page's data loads, so `initialRunning` can't
  // carry a DB timer; a page hydrates the shell once its own fetch resolves. This is
  // the local-vs-server reconciliation the interim effort seam deferred:
  //   - a seed fills an empty slot (a DB timer shown on load);
  //   - a seed with the same id refreshes the fields — the richer fn_my_timesheet
  //     shape (`what`, `over_8h`) over a leaner just-started one — keeping the tick;
  //   - a null or different-id seed NEVER removes or replaces a live timer: it was
  //     started from a task/thread and the page's fetch is simply behind.
  const hydrate = useCallback((seed) => {
    setRunning((cur) => {
      if (!seed) return cur;
      if (!cur) return withLabel(seed);
      if (cur.id === seed.id) return withLabel({ ...cur, ...seed });
      return cur;
    });
  }, []);

  const value = useMemo(
    () => ({ running, elapsedSeconds, start, stop, hydrate }),
    [running, elapsedSeconds, start, stop, hydrate],
  );

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
}

export function useTimer() {
  const ctx = useContext(TimerContext);
  if (!ctx) {
    throw new Error('useTimer must be used within a TimerProvider (shell)');
  }
  return ctx;
}

export default useTimer;
